/* We cannot use type `unknown` instead of `any` here because it will break the type assertion `isReactComponent` function is providing. */
/* eslint-disable @typescript-eslint/no-explicit-any */
import type React from "react";

type ReactComponent =
  | React.FC<{ className?: string }>
  | React.ComponentClass<{ className?: string }, unknown>;

/**
 * Checks if a given value is a function component.
 */
export const isFunctionComponent = (
  component: unknown,
): component is React.FC<{ className?: string }> => {
  return typeof component === "function";
};

/**
 * Checks if a given value is a class component.
 */
export const isClassComponent = (
  component: unknown,
): component is React.ComponentClass<{ className?: string }, unknown> => {
  return (
    typeof component === "function" &&
    component.prototype &&
    (!!component.prototype.isReactComponent || !!component.prototype.render)
  );
};

/**
 * Checks if a given value is a forward ref component.
 */
export const isForwardRefComponent = (
  component: unknown,
): component is React.ForwardRefExoticComponent<{ className?: string }> => {
  return (
    typeof component === "object" &&
    component !== null &&
    (component as { $$typeof: number }).$$typeof.toString() ===
      "Symbol(react.forward_ref)"
  );
};

/**
 * Checks if a given value is a valid React component.
 */
export const isReactComponent = (
  component: unknown,
): component is ReactComponent => {
  return (
    isFunctionComponent(component) ||
    isForwardRefComponent(component) ||
    isClassComponent(component)
  );
};
